import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, img } from "@/lib/api";
import { ImageUploader } from "@/pages/admin/ImageUploader";
import { toPayload } from "@/pages/admin/productRow";

export default function ProductDetailEditor({ product, onClose, onSaved }) {
  const [v, setV] = useState(null);
  useEffect(() => {
    setV(product ? { ...product, featuresText: (product.features || []).join("\n") } : null);
  }, [product]);
  if (!v) return null;

  const save = async () => {
    const body = toPayload({ ...v, features: v.featuresText.split("\n").map((s) => s.trim()).filter(Boolean) });
    delete body.featuresText;
    try {
      await api.put(`/admin/products/${v.id}`, body);
      toast.success("Ürün detayları kaydedildi");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="product-editor-dialog">
        <DialogHeader>
          <DialogTitle>{v.name}</DialogTitle>
          <DialogDescription>Resimler, açıklama ve ürün özellikleri</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="text-sm font-semibold mb-2">Ana resim</div>
            <div className="flex gap-4 items-center flex-wrap">
              <img src={img(v.image)} alt="" className="h-28 w-28 object-contain border rounded bg-white" data-testid="editor-main-image" />
              <ImageUploader testid="editor-main-upload" onUploaded={(url) => setV({ ...v, image: url })} />
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold mb-2">Ek resimler</div>
            <div className="flex gap-2 flex-wrap mb-2">
              {v.images.map((u) => (
                <div key={u} className="relative">
                  <img src={img(u)} alt="" className="h-20 w-20 object-contain border rounded bg-white" />
                  <button onClick={() => setV({ ...v, images: v.images.filter((x) => x !== u) })} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
            <ImageUploader testid="editor-extra-upload" onUploaded={(url) => setV({ ...v, images: [...v.images, url] })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm"><span className="font-semibold">Tür</span>
              <select data-testid="editor-subtype" value={v.sub_type} onChange={(e) => setV({ ...v, sub_type: e.target.value })} className="mt-1 w-full h-10 border rounded-md px-2">
                {["Kuru Mama", "Yaş Mama", "Ödül Maması", "Kum", "Aksesuar"].map((t) => <option key={t}>{t}</option>)}
              </select>
            </label>
            <label className="text-sm flex items-center gap-2 mt-6"><input data-testid="editor-active" type="checkbox" checked={v.active} onChange={(e) => setV({ ...v, active: e.target.checked })} className="h-4 w-4" /> Sitede yayında</label>
          </div>
          <label className="block text-sm"><span className="font-semibold">Açıklama</span>
            <textarea data-testid="editor-description" rows={3} value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} className="mt-1 w-full border rounded-md p-2" />
          </label>
          <label className="block text-sm"><span className="font-semibold">Özellikler (her satıra bir özellik)</span>
            <textarea data-testid="editor-features" rows={5} value={v.featuresText} onChange={(e) => setV({ ...v, featuresText: e.target.value })} placeholder={"Ham protein: %32\nTahılsız"} className="mt-1 w-full border rounded-md p-2 font-mono text-sm" />
          </label>
        </div>
        <DialogFooter>
          <button data-testid="editor-save-button" onClick={save} className="h-10 px-5 rounded-md bg-[#16A34A] text-white font-bold">Kaydet</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
